import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { Language, getTranslations } from '@/lib/i18n';

interface FeaturedDishesProps {
  language: Language;
}

const dishImages = [
  {
    id: 'dish_signature_pasta',
    price: 32,
    image: "https://images.unsplash.com/photo-1663721605989-3bdd2c994190",
    alt: 'Elegant plate of handmade pasta with black truffle shavings and wild mushrooms on white porcelain',
    isLarge: true,
  },
  {
    id: 'dish_sushi_platter',
    price: 48,
    image: "https://images.unsplash.com/photo-1734779335903-2e506e3de356",
    alt: 'Artfully arranged sushi platter with fresh nigiri, sashimi, and specialty rolls on black slate',
    isLarge: false,
  },
  {
    id: 'dish_wagyu_steak',
    price: 85,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_139cfd93b-1768735979375.png",
    alt: 'Perfectly grilled wagyu ribeye steak with caramelized crust, served with roasted vegetables',
    isLarge: false,
  },
];

export default function FeaturedDishes({ language }: FeaturedDishesProps) {
  const t = getTranslations(language);
  const f = t.home.featured;

  const dishes = dishImages.map((d, i) => ({
    ...d,
    name: f.dishes[i]?.name ?? '',
    description: f.dishes[i]?.description ?? '',
    badges: f.dishes[i]?.badges ?? [],
    category: f.dishes[i]?.category ?? '',
  }));

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-background">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 mb-16 animate-fade-in">
          <div className="max-w-2xl">
            <span className="text-accent text-xs font-bold tracking-[0.3em] uppercase mb-4 block">
              {f.sectionLabel}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-foreground">
              {f.title} <span className="italic font-light">{f.titleItalic}</span>
            </h2>
          </div>
          <Link
            href="/menu"
            className="group flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors">
            {f.viewFullMenu}
            <div className="w-12 h-12 rounded-full border border-border flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-all">
              <Icon name="ArrowRightIcon" size={20} className="group-hover:text-primary-foreground" />
            </div>
          </Link>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 min-h-[600px] animate-slide-up delay-200">
          {dishes?.map((dish) => (
            <div
              key={dish?.id}
              className={`${
                dish?.isLarge ? 'md:col-span-2 md:row-span-2' : ''
              } group relative rounded-[40px] overflow-hidden bg-card border border-border/50 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer min-h-[384px] ${
                dish?.isLarge ? 'min-h-[600px] md:h-full' : 'h-full'
              }`}>
              <div className="image-zoom-container h-full">
                <AppImage src={dish?.image} alt={dish?.alt} className="w-full h-full object-cover" />
              </div>
              <div className="gradient-overlay-bottom"></div>
              <div className="absolute inset-0 flex flex-col justify-end p-8 z-10">
                <div className="flex flex-wrap gap-2 mb-4">
                  {dish?.badges?.map((badge, badgeIndex) => (
                    <span
                      key={`badge_${dish?.id}_${badgeIndex}`}
                      className="px-3 py-1 rounded-full bg-primary/90 backdrop-blur-sm text-primary-foreground text-xs font-bold uppercase tracking-wider">
                      {badge}
                    </span>
                  ))}
                </div>
                <span className="text-accent text-[10px] font-bold tracking-[0.4em] uppercase mb-2 block">
                  {dish?.category}
                </span>
                <h3
                  className={`font-serif ${
                    dish?.isLarge ? 'text-4xl md:text-5xl' : 'text-2xl md:text-3xl'
                  } text-white mb-3 leading-tight`}>
                  {dish?.name}
                </h3>
                {dish?.isLarge && (
                  <p className="text-white/90 text-base md:text-lg leading-relaxed mb-6 max-w-lg">
                    {dish?.description}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-serif font-bold text-white">${dish?.price}</span>
                  {dish?.isLarge && (
                    <button className="px-6 py-3 bg-white text-foreground rounded-full text-sm font-bold uppercase tracking-wider hover:bg-accent hover:text-accent-foreground transition-all">
                      {f.orderNow}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}